import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { AlertTriangle, Clock, AlertCircle, Info, Download } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const TIPO_LABEL = {
  atraso: 'Atraso',
  saida_antecipada: 'Saída Antecipada',
  hora_extra: 'Hora Extra > 2h',
  intervalo: 'Intervalo < 1h',
  poucas_batidas: '< 4 Batidas',
  duplicada: 'Batida Duplicada'
};

const TIPO_ICON = {
  atraso: Clock,
  saida_antecipada: Clock,
  hora_extra: AlertCircle,
  intervalo: AlertTriangle,
  poucas_batidas: Info,
  duplicada: Info
};

const GRAVIDADE_COR = {
  baixa: 'bg-blue-100 text-blue-700',
  media: 'bg-amber-100 text-amber-700',
  alta: 'bg-red-100 text-red-700'
};

const DivergenciasPanel = ({ empresas, empresaFiltro }) => {
  const { user } = useAuth();
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dataInicio, setDataInicio] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
  });
  const [dataFim, setDataFim] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });

  const carregar = async () => {
    setLoading(true);
    setErro(null);
    try {
      const token = localStorage.getItem('token');
      const params = { data_inicio: dataInicio, data_fim: dataFim };
      if (empresaFiltro) params.empresa_id = empresaFiltro;
      const response = await axios.get(`${API}/api/banco-horas/divergencias`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      setDados(response.data);
    } catch (err) {
      console.error('Erro ao carregar divergências:', err);
      setErro(err.response?.data?.detail || err.message || 'Erro ao carregar divergências');
    } finally {
      setLoading(false);
    }
  };

  const exportarCSV = () => {
    if (!dados?.divergencias?.length) return;
    const BOM = '\uFEFF';
    const linhas = [['Funcionário', 'Data', 'Tipo', 'Descrição', 'Horários', 'Gravidade']];
    for (const d of dados.divergencias) {
      linhas.push([
        d.funcionario_nome,
        d.data,
        TIPO_LABEL[d.tipo] || d.tipo,
        d.descricao,
        (d.marcacoes || []).join('; '),
        d.gravidade
      ]);
    }
    const csv = BOM + linhas.map(l => l.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.setAttribute('download', `divergencias_${dataInicio}_${dataFim}.csv`);
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    carregar();
  }, [empresaFiltro]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-4 items-end">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Data Início</label>
          <input
            type="date"
            value={dataInicio}
            onChange={(e) => setDataInicio(e.target.value)}
            className="rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Data Fim</label>
          <input
            type="date"
            value={dataFim}
            onChange={(e) => setDataFim(e.target.value)}
            className="rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none text-sm"
          />
        </div>
        <Button onClick={carregar} disabled={loading}>
          {loading ? 'Carregando...' : 'Atualizar'}
        </Button>
        {dados?.divergencias?.length > 0 && (
          <Button onClick={exportarCSV} variant="outline">
            <Download className="h-4 w-4 mr-1" /> Exportar CSV
          </Button>
        )}
      </div>

      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="loading-spinner w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
        </div>
      )}

      {erro && (
        <Card className="border-red-300 bg-red-50">
          <CardContent className="py-6 text-center">
            <AlertTriangle className="mx-auto h-10 w-10 text-red-400 mb-2" />
            <p className="text-red-700 font-medium">Erro ao carregar divergências</p>
            <p className="text-red-600 text-sm mt-1">{erro}</p>
            <Button variant="outline" size="sm" className="mt-3" onClick={carregar}>Tentar novamente</Button>
          </CardContent>
        </Card>
      )}

      {!loading && !erro && dados && Object.keys(dados.resumo?.por_tipo || {}).length === 0 && dados.divergencias?.length === 0 && (
        <Card>
          <CardContent className="py-8 text-center text-gray-500">
            <AlertTriangle className="mx-auto h-12 w-12 text-gray-300 mb-2" />
            <p>Nenhuma divergência encontrada no período.</p>
          </CardContent>
        </Card>
      )}

      {!loading && dados && (
        <>
          {dados.resumo && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {Object.keys(TIPO_LABEL).map((tipo) => {
                const Icon = TIPO_ICON[tipo] || Info;
                const count = dados.resumo.por_tipo?.[tipo] || 0;
                return (
                  <Card key={tipo}>
                    <CardContent className="pt-4 text-center">
                      <Icon className="h-5 w-5 mx-auto text-gray-400 mb-1" />
                      <p className="text-xs text-gray-500">{TIPO_LABEL[tipo]}</p>
                      <p className="text-lg font-bold">{count}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {dados.divergencias && dados.divergencias.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  Divergências ({dados.divergencias.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-gray-500">
                        <th className="pb-2 font-medium">Funcionário</th>
                        <th className="pb-2 font-medium">Data</th>
                        <th className="pb-2 font-medium">Tipo</th>
                        <th className="pb-2 font-medium">Descrição</th>
                        <th className="pb-2 font-medium">Horários</th>
                        <th className="pb-2 font-medium text-right">Gravidade</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dados.divergencias.map((d, idx) => (
                        <tr key={idx} className="border-b last:border-0">
                          <td className="py-2">{d.funcionario_nome}</td>
                          <td className="py-2 text-gray-500">{d.data}</td>
                          <td className="py-2">
                            <Badge variant="outline" className="text-xs">
                              {TIPO_LABEL[d.tipo] || d.tipo}
                            </Badge>
                          </td>
                          <td className="py-2 max-w-xs truncate">{d.descricao}</td>
                          <td className="py-2 text-gray-500 text-xs whitespace-nowrap">
                            {d.marcacoes?.length ? d.marcacoes.join(' · ') : '-'}
                          </td>
                          <td className="py-2 text-right">
                            <Badge className={`text-xs ${GRAVIDADE_COR[d.gravidade] || 'bg-gray-100 text-gray-700'}`}>
                              {d.gravidade}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default DivergenciasPanel;
